import { loadEthers } from '@/utils/load-ethers';
import { getWalletProviderId } from '@/utils/auth-token';
import { ensurePrivyEmbeddedChain } from '@/utils/privy';
import { getWalletSigningProvider } from '@/utils/wallet-provider';
import { ensureWalletChainById } from '@/utils/wallet-chain';
import { sendContractTransaction, waitForContractTransaction } from '@/utils/privy-contract-tx';
import { DEFAULT_POLYGON_RPC_URL } from '@/utils/polygon-rpc';

const ERC20_TRANSFER_ABI = [
  'function transfer(address to, uint256 amount) returns (bool)',
  'function balanceOf(address owner) view returns (uint256)',
];

/** 只读余额用公共 RPC，避免选链时 wallet_switchEthereumChain 弹 MetaMask */
const READ_RPC_BY_CHAIN = {
  1: 'https://ethereum.publicnode.com',
  56: 'https://bsc-dataseed.binance.org',
  137: DEFAULT_POLYGON_RPC_URL,
  8453: 'https://base.publicnode.com',
  42161: 'https://arbitrum-one.publicnode.com',
};

function getSigningProvider() {
  return getWalletSigningProvider();
}

function resolveReadRpcUrl(chainId) {
  const id = Number(chainId);
  if (Number.isFinite(id) && READ_RPC_BY_CHAIN[id]) return READ_RPC_BY_CHAIN[id];
  if (id === getExpectedChainIdFallback()) {
    return String(process.env.VUE_APP_CHAIN_RPC ?? '').trim() || DEFAULT_POLYGON_RPC_URL;
  }
  return '';
}

function getExpectedChainIdFallback() {
  const parsed = Number(process.env.VUE_APP_CHAIN_ID);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 137;
}

async function ensureDepositChain(provider, chainId, meta = {}) {
  if (getWalletProviderId() === 'privy') {
    await ensurePrivyEmbeddedChain(chainId);
    return;
  }
  await ensureWalletChainById(provider, chainId, meta);
}

/**
 * 将人类可读金额转为 token 最小单位
 */
export async function toTokenUnits(amount, decimals = 6) {
  const { parseUnits } = await loadEthers();
  const dec = Number(decimals);
  const safeDec = Number.isFinite(dec) && dec >= 0 ? dec : 6;
  const raw = String(amount ?? '').trim();
  if (!raw) throw new Error('invalid transfer amount');
  return parseUnits(raw, safeDec);
}

/**
 * 只读 ERC20 余额（公共 RPC，不切 MetaMask 网络）
 */
export async function readTokenBalanceReadonly(
  contractAddress,
  ownerAddress,
  decimals = 6,
  chainId
) {
  const token = String(contractAddress ?? '').trim();
  const owner = String(ownerAddress ?? '').trim();
  if (!token || !owner) return 0;

  const rpcUrl = resolveReadRpcUrl(chainId);
  if (!rpcUrl) return 0;

  const { JsonRpcProvider, Contract, formatUnits } = await loadEthers();
  const provider = new JsonRpcProvider(rpcUrl, Number(chainId) || undefined);
  const contract = new Contract(token, ERC20_TRANSFER_ABI, provider);
  const raw = await contract.balanceOf(owner);
  const dec = Number(decimals);
  const safeDec = Number.isFinite(dec) && dec >= 0 ? dec : 6;
  return Number(formatUnits(raw, safeDec));
}

/**
 * 读取 ERC20 余额（人类可读）
 * @param {number|string} [chainId]
 * @param {{ switchChain?: boolean }} [options]
 *   - switchChain=false：公共 RPC 只读，不弹 MetaMask 切链（选网络/预览余额）
 *   - 默认 true：走钱包 provider，必要时切链
 */
export async function readTokenBalance(
  contractAddress,
  ownerAddress,
  decimals = 6,
  chainId,
  options = {}
) {
  if (options?.switchChain === false) {
    return readTokenBalanceReadonly(contractAddress, ownerAddress, decimals, chainId);
  }

  const token = String(contractAddress ?? '').trim();
  const owner = String(ownerAddress ?? '').trim();
  if (!token || !owner) return 0;

  const provider = await getSigningProvider();
  if (!provider) return 0;

  if (chainId != null) {
    await ensureDepositChain(provider, chainId);
  }

  const { BrowserProvider, Contract, formatUnits } = await loadEthers();
  const browserProvider = new BrowserProvider(provider);
  const contract = new Contract(token, ERC20_TRANSFER_ABI, browserProvider);
  const raw = await contract.balanceOf(owner);
  const dec = Number(decimals);
  const safeDec = Number.isFinite(dec) && dec >= 0 ? dec : 6;
  return Number(formatUnits(raw, safeDec));
}

/**
 * 发起 ERC20 充值转账（Privy 与 MetaMask 统一走 populate + sendContractTransaction）
 */
export async function sendRcTokenTransfer(params = {}) {
  const toAddress = String(params.toAddress ?? '').trim();
  const contractAddress = String(params.contractAddress ?? '').trim();
  const chainId = params.chainId;

  if (!toAddress) throw new Error('deposit address missing');
  if (!contractAddress) throw new Error('token contract missing');

  const provider = await getSigningProvider();
  if (!provider) throw new Error('wallet provider not found');

  await ensureDepositChain(provider, chainId, {
    chainName: params.chainName,
    nativeSymbol: params.nativeSymbol,
  });

  const { BrowserProvider, Contract } = await loadEthers();
  const browserProvider = new BrowserProvider(provider);
  const signer = await browserProvider.getSigner();
  const units = await toTokenUnits(params.amount, params.decimals ?? 6);
  if (units <= 0n) {
    throw new Error('invalid transfer amount');
  }

  const contract = new Contract(contractAddress, ERC20_TRANSFER_ABI, signer);
  const populated = await contract.transfer.populateTransaction(toAddress, units);
  const tx = await sendContractTransaction(signer, browserProvider, populated);
  const txHash = tx?.hash || '';

  if (params.waitForConfirmation === false) {
    return {
      txHash,
      wait: () => waitForContractTransaction(tx),
    };
  }

  const receipt = await waitForContractTransaction(tx);
  return {
    txHash: receipt?.hash || txHash,
  };
}

export { isUserRejectedError } from '@/utils/trade-error-map';
